/// <amd-module name="Search/Shared/Base/Stores/ZeroDataStore" />

import * as VSSStore from "VSS/Reflux/Store";
import * as SearchShared from "Search/LegacyWebApi/Contracts/Search.SharedLegacy";
import { ScenarioType } from "Search/Shared/Components/ZeroData/ZeroData.Props";
import { ISearchFailedPayload } from "Search/Shared/Base/ActionsHub";

/**
 * Common Error codes for code and workitem
 */
export namespace ErrorStrings {
    export const AccountIsBeingIndexed: string = "AccountIsBeingIndexed";
    export const AccountIsBeingReindexed: string = "AccountIsBeingReindexed";
    export const AccountIsBeingOnboarded: string = "AccountIsBeingOnboarded";
    export const AliasNotFoundErrorCode: string = "AliasNotFound";
    export const IndexingNotStartedErrorCode: string = "IndexingNotStarted";
    export const PrefixWildcardQueryNotSupported: string = "PrefixWildcardQueryNotSupported";
    export const BranchesAreBeingIndexed: string = "BranchesAreBeingIndexed";
    export const WorkItemsNotAccessible: string = "WorkItemsNotAccessible";
    export const EmptyQueryNotSupported: string = "EmptyQueryNotSupported";
    export const OnlyWildcardQueryNotSupported: string = "OnlyWildcardQueryNotSupported";
    export const ZeroResultsWithWildcard: string = "ZeroResultsWithWildcard";
    export const ZeroResultsWithFilter: string = "ZeroResultsWithFilter";
    export const ZeroResultsWithWildcardAndFilter: string = "ZeroResultsWithWildcardAndFilter";
    export const ZeroResultsWithNoWildcardNoFilter: string = "ZeroResultsWithNoWildcardNoFilter";
    export const WildcardQueriesWithCEFacetsNotSupported: string = "WildcardQueriesWithCEFacetsNotSupported";
    export const PhraseQueriesWithCEFacetsNotSupported: string = "PhraseQueriesWithCEFacetsNotSupported";
    export const UnsupportedProximitySearchTerm: string = "UnsupportedProximitySearchTerm";
    export const PrefixSuffixSubStringTooShort: string = "PrefixSuffixSubStringTooShort";
    export const SubstringWithInfixWildcard: string = "SubstringWithInfixWildcard";
    export const SubstringSearchCEFFacets: string = "SubstringSearchCEFFacets";
    export const SubstringWithInfixWildcardCEFFacets: string = "SubstringWithInfixWildcardCEFFacets";
    export const QuestionMarkWildcardSubstring: string = "QuestionMarkWildcardSubstring";
    export const MixedWildcardSubstring: string = "MixedWildcardSubstring";
}

export interface ZeroDataState {
    scenario: ScenarioType;
}

export class ZeroDataStore extends VSSStore.Store {
    protected _state: ZeroDataState = { scenario: ScenarioType.None } as ZeroDataState;

    public get state(): ZeroDataState {
        return this._state;
    }

    public onSearchFailed = (
        payload: ISearchFailedPayload<SearchShared.EntitySearchQuery>,
        query: SearchShared.EntitySearchQuery): void => {
        if (this.areQueriesSame(payload.query, query)) {
            this._state.scenario = ScenarioType.ServiceError;
            this.emitChanged();
        }
    }

    public onLandingPage = (): void => {
        this._state.scenario = ScenarioType.LandingPage;
        this.emitChanged();
    }

    public reset = (): void => {
        this._state.scenario = ScenarioType.None;
        this.emitChanged();
    }

    /**
     * Return the results after comparing the queries.
     * @param queryInState
     * @param queryInPayload
     */
    protected areQueriesSame(
        queryInState: SearchShared.EntitySearchQuery,
        queryInPayload: SearchShared.EntitySearchQuery): boolean {
        return true;
    }
}

export function getScenario(
    errorData: SearchShared.ErrorData[],
    totalResultsCount: number,
    takeResults: number,
    showMoreScenario: boolean): ScenarioType {
    const isPrefixErrorScenario = check(errorData, ErrorStrings.PrefixWildcardQueryNotSupported),
        isProjectIndexingScenario = check(
            errorData,
            ErrorStrings.AliasNotFoundErrorCode,
            ErrorStrings.IndexingNotStartedErrorCode,
            ErrorStrings.AccountIsBeingOnboarded),
        isWorkItemNotAccessibleScenario = check(errorData, ErrorStrings.WorkItemsNotAccessible),
        isEmptyExpressionScenario = check(errorData, ErrorStrings.EmptyQueryNotSupported),
        isOnlyWildCardNotSupported = check(errorData, ErrorStrings.OnlyWildcardQueryNotSupported),
        isZeroResultsWithWildcard = check(errorData, ErrorStrings.ZeroResultsWithWildcard),
        isZeroResultsWithFilter = check(errorData, ErrorStrings.ZeroResultsWithFilter),
        isZeroResultsWithWildcardAndFilter = check(errorData, ErrorStrings.ZeroResultsWithWildcardAndFilter),
        isZeroResultsWithNoWildcardNoFilter = check(errorData, ErrorStrings.ZeroResultsWithNoWildcardNoFilter),
        isWildcardQueriesWithCEFacetsNotSupported = check(errorData, ErrorStrings.WildcardQueriesWithCEFacetsNotSupported),
        isPhraseQueriesWithCEFacetsNotSupported = check(errorData, ErrorStrings.PhraseQueriesWithCEFacetsNotSupported),
        isUnsupportedProximityScenario = check(errorData, ErrorStrings.UnsupportedProximitySearchTerm),
        isSubstringTooShort = check(errorData, ErrorStrings.PrefixSuffixSubStringTooShort),
        isPrefixInfixSuffixWildcardSubstring = check(errorData, ErrorStrings.SubstringWithInfixWildcard),
        isCEFSubString = check(errorData, ErrorStrings.SubstringSearchCEFFacets),
        isCEFSubStringWithInfixWildcard = check(errorData, ErrorStrings.SubstringWithInfixWildcardCEFFacets),
        isQuestionMarkWildcardSubstring = check(errorData, ErrorStrings.QuestionMarkWildcardSubstring) || check(errorData, ErrorStrings.MixedWildcardSubstring);

    if (isProjectIndexingScenario) {
        return ScenarioType.IndexingScenario;
    }

    if (isPrefixErrorScenario) {
        return ScenarioType.PrefixWildCardNotSupported;
    }

    if (isWorkItemNotAccessibleScenario) {
        return ScenarioType.NoPermission;
    }

    if (isEmptyExpressionScenario) {
        return ScenarioType.EmptyQueryNotSupported;
    }

    if (isOnlyWildCardNotSupported) {
        return ScenarioType.OnlyWildcardQueryNotSupported;
    }

    if (isPhraseQueriesWithCEFacetsNotSupported) {
        return ScenarioType.PhraseQueriesWithCEFacetsNotSupported;
    }

    if (isWildcardQueriesWithCEFacetsNotSupported) {
        return ScenarioType.WildcardQueriesWithCEFacetsNotSupported;
    }

    if (isZeroResultsWithWildcard) {
        return ScenarioType.ZeroResultsWithWildcard;
    }

    if (isZeroResultsWithFilter) {
        return ScenarioType.ZeroResultsWithFilter;
    }

    if (isZeroResultsWithWildcardAndFilter) {
        return ScenarioType.ZeroResultsWithWildcardAndFilter;
    }

    if (isZeroResultsWithNoWildcardNoFilter) {
        return ScenarioType.ZeroResultsWithNoWildcardNoFilter;
    }

    if( isUnsupportedProximityScenario){
        return ScenarioType.UnsupportedProximitySearchTerm;
    }

    if(isSubstringTooShort){
        return ScenarioType.SubStringTooShort;
    }

    if(isPrefixInfixSuffixWildcardSubstring || isCEFSubStringWithInfixWildcard){
        return ScenarioType.SubStringWithInfixWildcard;
    }

    if(isCEFSubString){
        return ScenarioType.CEFFacetSubstring;
    }

    if (isQuestionMarkWildcardSubstring) {
        return ScenarioType.QuestionMarkWildcardSubstring;
    }

    if (totalResultsCount <= 0) {
        return ScenarioType.ZeroResults;
    }

    if (totalResultsCount <= takeResults) {
        return ScenarioType.NoPermission;
    }
    else {
        // Lets present user with a show more link if one hasn't clicked it already.
        return !showMoreScenario
            ? ScenarioType.NoPermissionWithShowMore
            : ScenarioType.NoPermissionAfterShowMore;
    }
}

function check(errors: SearchShared.ErrorData[], ...args: string[]): boolean {
    return errors
        .some(e => args.some((arg: string) => e.errorCode === arg));
}